import { useRef, useEffect } from "react";
import getExpandedTreePaths from "@/bundles/utils/getExpandedTreePaths";
import findCurrentContentIdTreeRoute from "@/bundles/utils/findCurrentContentIdTreeRoute";

type UseCollapseTreeParams = {
  expandedItems: string[];
  setExpandedItems: (items: string[]) => void;
  treeItems: Record<string, any>;
};

const useCollapseTree = ({
  expandedItems,
  setExpandedItems,
  treeItems,
}: UseCollapseTreeParams) => {
  const prevExpandedRef = useRef<string[]>(expandedItems);

  const buildBranchForLeaf = (leafId: string | null): string[] => {
    if (!leafId) return [];
    const route = findCurrentContentIdTreeRoute(treeItems, leafId);
    if (!route) return [];
    return getExpandedTreePaths(route);
  };

  const equalList = (a: string[], b: string[]) => {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  };

  useEffect(() => {
    const prev = prevExpandedRef.current;
    const next = expandedItems;
    if (next.length === 0) {
      prevExpandedRef.current = next;
      return;
    }
    const added = next.find((x) => !prev.includes(x));
    const removed = prev.filter((x) => !next.includes(x));
    let targetComposite: string | null = null;

    if (added) {
      targetComposite = added;
    } else if (removed.length) {
      removed.sort((a, b) => a.split("/").length - b.split("/").length);

      const shallow = removed[0];
      if (!shallow) return;
      const parts = shallow.split("/").filter(Boolean);
      targetComposite = parts.length > 1 ? parts.slice(0, -1).join("/") : null;
    } else {
      if (next.length > 0) {
        targetComposite =
          next.reduce((a, b) => {
            if (!a) return b;
            if (!b) return a;
            return a.split("/").length >= b.split("/").length ? a : b;
          }, next[0]) ?? null;
      } else {
        targetComposite = null;
      }
    }

    const leafId = targetComposite
      ? (targetComposite.split("/").pop() ?? null)
      : null;
    const normalized = buildBranchForLeaf(leafId);

    if (!equalList(next, normalized)) {
      setExpandedItems(normalized);
      prevExpandedRef.current = normalized;
    } else {
      prevExpandedRef.current = next;
    }
  }, [expandedItems, treeItems, setExpandedItems]);
};

export default useCollapseTree;
